import {
  AcademicCapIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  HeartIcon,
  ScaleIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import React, { useState } from 'react';

const Donate: React.FC = () => {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [donationType, setDonationType] = useState<'once' | 'monthly'>('once');
  const [showSuccess, setShowSuccess] = useState(false);

  const presetAmounts = [25, 50, 100, 250, 500, 1000];

  const impactAreas = [
    {
      title: 'Legal Advocacy',
      description: 'Supporting legal representation and policy advocacy for Black Canadians.',
      icon: ScaleIcon,
      color: 'text-blue-600'
    },
    {
      title: 'Education Programs',
      description: 'Funding scholarships, workshops, and educational resources.',
      icon: AcademicCapIcon,
      color: 'text-green-600'
    },
    {
      title: 'Community Outreach',
      description: 'Expanding our reach to support more Black Canadian communities.',
      icon: UserGroupIcon,
      color: 'text-purple-600'
    }
  ];

  const getDonationAmount = () => {
    if (selectedAmount) return selectedAmount;
    if (customAmount) return parseFloat(customAmount);
    return 0;
  };

  // PayPal Configuration - automatically switches between sandbox and production
  const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';
  const paypalClientId = isDevelopment 
    ? process.env.REACT_APP_PAYPAL_CLIENT_ID_SANDBOX 
    : process.env.REACT_APP_PAYPAL_CLIENT_ID_PRODUCTION;

  const paypalInitialOptions = {
    clientId: paypalClientId || "",
    currency: "CAD",
    intent: "capture",
    // Use sandbox for development, live for production
    ...(isDevelopment && { "data-client-token": "sandbox" })
  };

  const createOrder = (data: any, actions: any) => {
    const amount = getDonationAmount();
    if (amount <= 0) return Promise.reject(new Error("Invalid amount"));

    return actions.order.create({
      purchase_units: [
        {
          amount: {
            currency_code: "CAD",
            value: amount.toString(),
          },
          description: `${donationType === 'monthly' ? 'Monthly ' : ''}Donation to PBCN`,
        },
      ],
    });
  };

  const onApprove = (data: any, actions: any) => {
    return actions.order.capture().then((details: any) => {
      setShowSuccess(true);
      // Here you would typically send the transaction details to your backend
      console.log("Transaction completed by " + details.payer.name.given_name);
    });
  };

  const onError = (err: any) => {
    console.error("PayPal error:", err);
    alert("There was an error processing your donation. Please try again.");
  };

  if (showSuccess) {
    return (
      <div className="flex items-center justify-center min-h-screen pt-20 bg-gray-50">
        <div className="max-w-md p-8 mx-auto text-center bg-white shadow-lg rounded-xl">
          <CheckCircleIcon className="w-16 h-16 mx-auto mb-4 text-green-600" />
          <h2 className="mb-4 text-2xl font-bold text-gray-900">Thank You!</h2>
          <p className="mb-6 text-gray-600">
            Your generous donation will help us continue our mission of empowering Black Canadians. 
            You will receive a confirmation email shortly.
          </p>
          <button
            onClick={() => {
              setShowSuccess(false);
              setSelectedAmount(null);
              setCustomAmount('');
            }}
            className="px-6 py-3 text-white transition-colors duration-200 rounded-lg bg-primary-600 hover:bg-primary-700"
          >
            Make Another Donation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 text-white bg-gradient-to-r from-primary-800 to-primary-900">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center">
            <HeartIcon className="w-16 h-16 mx-auto mb-6 text-secondary-400" />
            <h1 className="mb-6 text-4xl font-bold md:text-5xl font-heading">
              Support Our Mission
            </h1>
            <p className="max-w-3xl mx-auto text-xl text-gray-200 md:text-2xl">
              Your donation helps us advocate for equality, provide educational resources, 
              and build stronger communities for Black Canadians across the country.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-16 bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl font-heading">
              Your Impact
            </h2>
            <p className="text-xl text-gray-600">
              See how your donation makes a difference in our community
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {impactAreas.map((area) => (
              <div
                key={area.title}
                className="p-6 transition-shadow duration-300 bg-white shadow-lg rounded-xl hover:shadow-xl"
              >
                <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-lg bg-gray-50">
                  <area.icon className={`h-6 w-6 ${area.color}`} />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  {area.title}
                </h3>
                <p className="leading-relaxed text-gray-600">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Form */}
      {/* <section className="py-16">
        <div className="max-w-4xl px-4 mx-auto sm:px-6 lg:px-8">
          <div className="p-8 bg-white shadow-lg rounded-xl">
            <h2 className="mb-8 text-3xl font-bold text-center text-gray-900 font-heading">
              Make a Donation
            </h2>

            
            <div className="mb-8">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Donation Type</h3>
              <div className="flex flex-col gap-4 sm:flex-row">
                <button
                  onClick={() => setDonationType('once')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-colors duration-200 ${
                    donationType === 'once'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:border-primary-300'
                  }`}
                >
                  <CurrencyDollarIcon className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">One-time Donation</div>
                  <div className="text-sm">Make a single contribution</div>
                </button>
                <button
                  onClick={() => setDonationType('monthly')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-colors duration-200 ${
                    donationType === 'monthly'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:border-primary-300'
                  }`}
                >
                  <HeartIcon className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">Monthly Donation</div>
                  <div className="text-sm">Support us every month</div>
                </button>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Select Amount (CAD)</h3>
              <div className="grid grid-cols-2 gap-4 mb-4 md:grid-cols-3">
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className={`p-4 rounded-lg border-2 transition-colors duration-200 ${
                      selectedAmount === amount
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-gray-300 text-gray-700 hover:border-primary-300'
                    }`}
                  >
                    <div className="text-xl font-bold">${amount}</div>
                    {donationType === 'monthly' && <div className="text-sm">/month</div>}
                  </button>
                ))}
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-gray-700">$</span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setSelectedAmount(null);
                  }}
                  placeholder="Enter custom amount"
                  min="1"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {donationType === 'monthly' && <span className="text-gray-700">/month</span>}
              </div>
            </div>

            {getDonationAmount() > 0 && (
              <div className="mb-8">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Complete Your Donation</h3>
                <div className="p-6 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-700">
                      {donationType === 'monthly' ? 'Monthly' : 'One-time'} Donation:
                    </span>
                    <span className="text-2xl font-bold text-primary-700">
                      ${getDonationAmount().toFixed(2)} CAD
                    </span>
                  </div>
                  
                  {paypalClientId ? (
                    <PayPalScriptProvider options={paypalInitialOptions}>
                      <PayPalButtons
                        style={{ layout: "vertical" }}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onError={onError}
                      />
                    </PayPalScriptProvider>
                  ) : (
                    <div className="p-4 text-center border border-yellow-200 rounded-lg bg-yellow-50">
                      <p className="text-yellow-800">
                        PayPal integration is not configured for {isDevelopment ? 'development' : 'production'} environment.
                        {isDevelopment ? ' Please set up your PayPal Sandbox Client ID.' : ' Please set up your PayPal Production Client ID.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="p-6 rounded-lg bg-blue-50">
              <h3 className="mb-2 text-lg font-semibold text-gray-900">Tax Receipt Information</h3>
              <p className="text-gray-700">
                PBCN is a registered Canadian Non Profit. Tax receipts will be issued for all donations 
                over $20 CAD. You will receive your tax receipt via email within 5-7 business days.
              </p>
            </div>
          </div>
          <div className="p-8 bg-white shadow-lg rounded-xl">
            <h2 className="mb-8 text-3xl font-bold text-center text-gray-900 font-heading">
              Make a Donation
            </h2>

            <div className="mb-8">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Donation Type</h3>
              <div className="flex flex-col gap-4 sm:flex-row">
                <button
                  onClick={() => setDonationType('once')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-colors duration-200 ${
                    donationType === 'once'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:border-primary-300'
                  }`}
                >
                  <CurrencyDollarIcon className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">One-time Donation</div>
                  <div className="text-sm">Make a single contribution</div>
                </button>
                <button
                  onClick={() => setDonationType('monthly')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-colors duration-200 ${
                    donationType === 'monthly'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:border-primary-300'
                  }`}
                >
                  <HeartIcon className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">Monthly Donation</div>
                  <div className="text-sm">Support us every month</div>
                </button>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Select Amount (CAD)</h3>
              <div className="grid grid-cols-2 gap-4 mb-4 md:grid-cols-3">
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className={`p-4 rounded-lg border-2 transition-colors duration-200 ${
                      selectedAmount === amount
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-gray-300 text-gray-700 hover:border-primary-300'
                    }`}
                  >
                    <div className="text-xl font-bold">${amount}</div>
                    {donationType === 'monthly' && <div className="text-sm">/month</div>}
                  </button>
                ))}
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-gray-700">$</span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setSelectedAmount(null);
                  }}
                  placeholder="Enter custom amount"
                  min="1"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {donationType === 'monthly' && <span className="text-gray-700">/month</span>}
              </div>
            </div>

            {getDonationAmount() > 0 && (
              <div className="mb-8">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Complete Your Donation</h3>
                <div className="p-6 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-700">
                      {donationType === 'monthly' ? 'Monthly' : 'One-time'} Donation:
                    </span>
                    <span className="text-2xl font-bold text-primary-700">
                      ${getDonationAmount().toFixed(2)} CAD
                    </span>
                  </div>
                  
                  {paypalClientId ? (
                    <PayPalScriptProvider options={paypalInitialOptions}>
                      <PayPalButtons
                        style={{ layout: "vertical" }}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onError={onError}
                      />
                    </PayPalScriptProvider>
                  ) : (
                    <div className="p-4 text-center border border-yellow-200 rounded-lg bg-yellow-50">
                      <p className="text-yellow-800">
                        PayPal integration is not configured for {isDevelopment ? 'development' : 'production'} environment.
                        {isDevelopment ? ' Please set up your PayPal Sandbox Client ID.' : ' Please set up your PayPal Production Client ID.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="p-6 rounded-lg bg-blue-50">
              <h3 className="mb-2 text-lg font-semibold text-gray-900">Tax Receipt Information</h3>
              <p className="text-gray-700">
                PBCN is a registered Canadian Non Profit. Tax receipts will be issued for all donations 
                over $20 CAD. You will receive your tax receipt via email within 5-7 business days.
              </p>
            </div>
          </div>
        </div> 
      </section> */}
      {/* Domntion Form is in maintenance */}
      <section>
        <div className='px-4 mx-auto text-center bg-red-400 max-w-7xl sm:px-6 lg:px-8'>
          <p className="text-white py-11">
            Our donation form is currently undergoing maintenance. We appreciate your patience and understanding.
          </p>
        </div>
      </section>

      {/* Other Ways to Help */}
      <section className="py-16 bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl font-heading">
              Other Ways to Help
            </h2>
            <p className="text-xl text-gray-600">
              Support our mission beyond monetary donations
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="p-6 text-center bg-white shadow-lg rounded-xl">
              <UserGroupIcon className="w-12 h-12 mx-auto mb-4 text-primary-600" />
              <h3 className="mb-3 text-xl font-semibold text-gray-900">Volunteer</h3>
              <p className="mb-4 text-gray-600">
                Join our team of volunteers and help us organize events and outreach programs.
              </p>
              <a
                href="/contact"
                className="font-semibold text-primary-600 hover:text-primary-700"
              >
                Learn More →
              </a>
            </div>
            <div className="p-6 text-center bg-white shadow-lg rounded-xl">
              <HeartIcon className="w-12 h-12 mx-auto mb-4 text-primary-600" />
              <h3 className="mb-3 text-xl font-semibold text-gray-900">Spread Awareness</h3>
              <p className="mb-4 text-gray-600">
                Share our mission with friends and family to help expand our reach.
              </p>
              <a
                href="/about"
                className="font-semibold text-primary-600 hover:text-primary-700"
              >
                Learn More →
              </a>
            </div>
            <div className="p-6 text-center bg-white shadow-lg rounded-xl">
              <AcademicCapIcon className="w-12 h-12 mx-auto mb-4 text-primary-600" />
              <h3 className="mb-3 text-xl font-semibold text-gray-900">Corporate Partnership</h3>
              <p className="mb-4 text-gray-600">
                Partner with us to create workplace diversity and inclusion programs.
              </p>
              <a
                href="/contact"
                className="font-semibold text-primary-600 hover:text-primary-700"
              >
                Learn More →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
